import { useCallback, useMemo } from "react";
import { useFormContext } from "react-hook-form";

import { Label, Select, SelectItem } from "@fronterahealth/frontera-ui-components";

import { SingleSelectSection } from "@components/AssessmentFormRenderer/types/layoutTypes";
import { getNestedError } from "@components/AssessmentFormRenderer/utils/getNestedError";

interface FormSingleSelectProps {
  section: SingleSelectSection;
}

export const FormSingleSelect: React.FC<FormSingleSelectProps> = ({ section }) => {
  const fieldKey = section.key;
  const { setValue, watch, formState } = useFormContext();
  const error = getNestedError(formState.errors, fieldKey);
  const watchValue = watch(fieldKey);

  // Stable mapping from SelectItem.id (index as string) back to the section
  // option, so we can recover the underlying value when the user picks an item.
  const items: SelectItem[] = useMemo(
    () =>
      section.options.map((opt, i) => ({
        primary: opt.label,
        id: String(i),
      })),
    [section.options],
  );

  const selectedOption = section.options.find((opt) => opt.value === watchValue);
  const selectedItem = selectedOption ? items[section.options.indexOf(selectedOption)] : null;
  const hasValue = watchValue != null && watchValue !== "";
  const isInvalidValue = hasValue && !selectedOption;

  const handleSelect = useCallback(
    (item: SelectItem) => {
      const idx = item.id != null ? Number(item.id) : -1;
      const opt = section.options[idx];
      if (!opt) return;
      setValue(fieldKey, opt.value, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    },
    [fieldKey, section.options, setValue],
  );

  if (!section.editable) {
    return (
      <div className="flex flex-col gap-2">
        <Label>{section.label}</Label>
        <p className={`text-sm ${isInvalidValue ? "text-red-600" : "text-gray-700"}`}>
          {selectedOption ? selectedOption.label : hasValue ? String(watchValue) : "—"}
        </p>
        {isInvalidValue && (
          <p className="text-xs text-red-600">
            Not a recognized value. Allowed: {section.options.map((o) => o.label).join(", ")}.
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <Label>{section.label}</Label>
      {isInvalidValue && (
        <div className="rounded-md border border-red-500 bg-red-50 p-2 text-sm text-red-700">
          <p className="font-medium">Current value is not a recognized option.</p>
          <p className="mt-1 break-words">
            Found: <span className="font-mono">{String(watchValue)}</span>
          </p>
          <p className="mt-1">Please select a valid option below.</p>
        </div>
      )}
      <Select
        // Empty string suppresses Select's own internal label (it only
        // renders when `title.length` is truthy) — we already show the
        // label above via our own <Label>, matching this form's styling.
        title=""
        items={items}
        placeholderText={section.placeholder ?? "Select an option..."}
        selected={selectedItem}
        setSelected={handleSelect}
        errorMessage={error && !Array.isArray(error) && error.message ? String(error.message) : undefined}
      />
    </div>
  );
};
